import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    Legend,
    BarChart,
    Bar,
    ReferenceLine,
} from "recharts";
import { TrendingUp, TrendingDown, Wallet, Car as CarIcon, ArrowRight, Plus, Wrench, FileImage, AlertTriangle } from "lucide-react";
import { api, API, fmtMXN, fmtDate, todayISO, daysAgoISO, withToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";

const PERIODS = [
    { value: "7", label: "Últimos 7 días" },
    { value: "30", label: "Últimos 30 días" },
    { value: "90", label: "Últimos 90 días" },
    { value: "365", label: "Último año" },
];
const PIE_COLORS = ["#0F172A", "#475569", "#94A3B8", "#CBD5E1", "#E11D48", "#F43F5E", "#F59E0B"];
const CHART_TICK_STYLE = { fontSize: 11, fill: "#64748B" };
const CHART_TOOLTIP_STYLE = { borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 };
const CHART_LEGEND_STYLE_11 = { fontSize: 11 };
const CHART_LEGEND_STYLE_12 = { fontSize: 12 };

function ytdDelta(current, previous, lowerIsBetter, yearLabel) {
    current = current || 0;
    previous = previous || 0;
    if (previous === 0 && current === 0) {
        return { text: "Sin cambio", cls: "text-slate-400" };
    }
    if (previous === 0) {
        const improved = lowerIsBetter ? current <= 0 : current > 0;
        return { text: `Nuevo vs ${yearLabel}`, cls: improved ? "text-emerald-600" : "text-rose-500" };
    }
    const pct = ((current - previous) / Math.abs(previous)) * 100;
    const improved = lowerIsBetter ? current <= previous : current >= previous;
    const arrow = pct >= 0 ? "▲" : "▼";
    return { text: `${arrow} ${Math.abs(pct).toFixed(0)}% vs ${yearLabel}`, cls: improved ? "text-emerald-600" : "text-rose-500" };
}

function netMarginBadge(ytd) {
    if (!ytd || !ytd.income) {
        return { cls: "bg-slate-100 text-slate-700", title: "Sin ingresos YTD registrados" };
    }
    const margin = (ytd.net / ytd.income) * 100;
    if (margin <= 50) {
        return { cls: "bg-rose-100 text-rose-700", title: `Neto YTD = ${margin.toFixed(0)}% de Ingresos YTD` };
    }
    if (margin < 80) {
        return { cls: "bg-amber-100 text-amber-700", title: `Neto YTD = ${margin.toFixed(0)}% de Ingresos YTD` };
    }
    return { cls: "bg-emerald-100 text-emerald-700", title: `Neto YTD = ${margin.toFixed(0)}% de Ingresos YTD` };
}

function expirationAlert(exp) {
    if (!exp) return null;
    const labels = { insurance: "Seguro", verification: "Verificación", circulation_card: "Tarjeta de circulación" };
    const urgent = [];
    for (const key of Object.keys(labels)) {
        const doc = exp[key];
        if (doc && doc.days_left !== null && doc.days_left <= 30) {
            const when = doc.days_left < 0 ? `vencido hace ${Math.abs(doc.days_left)} d` : `vence en ${doc.days_left} d`;
            urgent.push(`${labels[key]} ${when} (${fmtDate(doc.date)})`);
        }
    }
    if (urgent.length === 0) return null;
    return urgent.join(" · ");
}

function PerformanceStackedTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    if (d.noIncome) {
        return (
            <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 shadow-sm">
                <p className="text-xs font-semibold text-slate-700 mb-1">{label}</p>
                <p className="text-xs text-rose-600">Gastos: {fmtMXN(d.expense)} · sin ingresos registrados</p>
                <p className="text-[11px] text-slate-400 mt-1 pt-1 border-t border-slate-100">No se puede calcular % (Ingresos = $0)</p>
            </div>
        );
    }
    return (
        <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 shadow-sm">
            <p className="text-xs font-semibold text-slate-700 mb-1">{label}</p>
            <p className="text-xs text-rose-600">Gastos: {d.expense_pct.toFixed(0)}% · {fmtMXN(d.expense)}</p>
            <p className="text-xs text-emerald-600">Neto: {d.net_pct.toFixed(0)}% · {fmtMXN(d.net)}</p>
            <p className="text-[11px] text-slate-400 mt-1 pt-1 border-t border-slate-100">Ingresos: {fmtMXN(d.income)}</p>
        </div>
    );
}

export default function Dashboard() {
    const { canWrite } = useAuth();
    const [period, setPeriod] = useState("30");
    const [carId, setCarId] = useState("all");
    const [startDate, setStartDate] = useState(daysAgoISO(30));
    const [endDate, setEndDate] = useState(todayISO());
    const [cars, setCars] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [ytdByCar, setYtdByCar] = useState({});
    const [ytdPrevByCar, setYtdPrevByCar] = useState({});
    const [prevYearLabel, setPrevYearLabel] = useState("");
    const [expirationsByCar, setExpirationsByCar] = useState({});
    const [serviceLogs, setServiceLogs] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (period !== "custom") {
            setStartDate(daysAgoISO(Number(period)));
            setEndDate(todayISO());
        }
    }, [period]);

    useEffect(() => {
        api.get("/cars").then((r) => setCars(r.data));
        api.get("/suppliers").then((r) => setSuppliers(r.data));
        api.get("/notifications").then((r) => setNotifications(r.data));
        api.get("/analytics/expirations").then((r) => {
            const map = {};
            r.data.forEach((e) => { map[e.car_id] = e; });
            setExpirationsByCar(map);
        });
        const today = todayISO();
        const [y, m, d] = today.split("-");
        const lastYear = String(Number(y) - 1);
        setPrevYearLabel(lastYear);
        api.get("/analytics/summary", { params: { start_date: `${y}-01-01`, end_date: today } }).then((r) => {
            const map = {};
            (r.data.by_car || []).forEach((b) => { map[b.car_id] = b; });
            setYtdByCar(map);
        });
        let prevEnd = `${lastYear}-${m}-${d}`;
        const check = new Date(`${prevEnd}T00:00:00Z`);
        if (Number.isNaN(check.getTime()) || check.getUTCMonth() + 1 !== Number(m)) {
            prevEnd = `${lastYear}-02-28`;
        }
        api.get("/analytics/summary", { params: { start_date: `${lastYear}-01-01`, end_date: prevEnd } }).then((r) => {
            const map = {};
            (r.data.by_car || []).forEach((b) => { map[b.car_id] = b; });
            setYtdPrevByCar(map);
        });
    }, []);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            const params = { start_date: startDate, end_date: endDate };
            if (carId !== "all") params.car_id = carId;
            const [{ data }, { data: logs }] = await Promise.all([
                api.get("/analytics/summary", { params }),
                api.get("/service-logs", { params }),
            ]);
            setSummary(data);
            setServiceLogs(logs);
            setLoading(false);
        };
        load();
    }, [startDate, endDate, carId]);

    const expenseByCategory = useMemo(() => {
        if (!summary) return [];
        return summary.by_category.filter((c) => c.type === "expense").sort((a, b) => b.total - a.total);
    }, [summary]);

    const performanceByCar = useMemo(() => {
        return (summary?.by_car || []).map((b) => {
            const income = b.income || 0;
            const expense = b.expense || 0;
            const net = b.net || 0;
            const noIncome = income <= 0 && expense > 0;
            const expense_pct = income > 0 ? (expense / income) * 100 : (noIncome ? 100 : 0);
            const net_pct = income > 0 ? (Math.max(net, 0) / income) * 100 : 0;
            return { label: b.label, income, expense, net, expense_pct, net_pct, noIncome };
        });
    }, [summary]);

    const fleetAvgMarginPct = summary?.total_income > 0
        ? (summary.net / summary.total_income) * 100
        : null;

    const carLabel = (id) => {
        const c = cars.find((c) => c.id === id);
        return c ? `${c.plate} · ${c.brand} ${c.model}` : "—";
    };
    const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
    const totalServiceCost = serviceLogs.reduce((acc, s) => acc + (s.cost || 0), 0);
    const groupedServiceLogs = useMemo(() => {
        const groups = {};
        serviceLogs.forEach((s) => {
            if (!groups[s.car_id]) groups[s.car_id] = [];
            groups[s.car_id].push(s);
        });
        return cars
            .filter((c) => groups[c.id])
            .map((c) => ({
                car: c,
                logs: groups[c.id],
                subtotal: groups[c.id].reduce((acc, s) => acc + (s.cost || 0), 0),
            }));
    }, [serviceLogs, cars]);

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Panel principal</p>
                    <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mt-1 font-display">
                        Resumen de la flotilla
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Indicadores clave, tendencias y alertas administrativas.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={carId} onValueChange={setCarId}>
                        <SelectTrigger className="w-[180px] bg-white" data-testid="filter-car">
                            <SelectValue placeholder="Auto" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos los autos</SelectItem>
                            {cars.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                    {c.plate} · {c.brand} {c.model}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-[180px] bg-white" data-testid="filter-period">
                            <SelectValue placeholder="Periodo" />
                        </SelectTrigger>
                        <SelectContent>
                            {PERIODS.map((p) => (
                                <SelectItem key={p.value} value={p.value}>
                                    {p.label}
                                </SelectItem>
                            ))}
                            <SelectItem value="custom">Personalizado</SelectItem>
                        </SelectContent>
                    </Select>
                    {period === "custom" && (
                        <div className="flex items-center gap-2">
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                data-testid="filter-start-date"
                                className="bg-white"
                            />
                            <span className="text-slate-400">→</span>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                data-testid="filter-end-date"
                                className="bg-white"
                            />
                        </div>
                    )}
                </div>
            </header>

            {/* Notifications */}
            {notifications.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="notifications-banner">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="text-sm font-semibold text-amber-900">
                                Tienes {notifications.length} {notifications.length === 1 ? "alerta" : "alertas"} próximas
                            </div>
                            <ul className="mt-2 text-sm text-amber-800 space-y-1">
                                {notifications.slice(0, 3).map((n) => (
                                    <li key={`${n.kind}-${n.id}`} className="flex items-center gap-2">
                                        <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200">
                                            {n.days_left < 0 ? `Vencido` : `${n.days_left} días`}
                                        </Badge>
                                        <span className="font-medium">{n.title}</span>
                                        <span className="text-amber-700">— {fmtDate(n.due_date)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <Link to="/recordatorios">
                            <Button variant="secondary" className="bg-white border border-amber-300 text-amber-900 hover:bg-amber-100">
                                Ver todas
                            </Button>
                        </Link>
                    </div>
                </div>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <KpiCard
                    label="Ingresos"
                    value={fmtMXN(summary?.total_income || 0)}
                    icon={<TrendingUp className="text-emerald-600" size={20} />}
                    accent="emerald"
                    testid="kpi-income"
                />
                <KpiCard
                    label="Gastos"
                    value={fmtMXN(summary?.total_expense || 0)}
                    icon={<TrendingDown className="text-rose-600" size={20} />}
                    accent="rose"
                    testid="kpi-expense"
                />
                <KpiCard
                    label="Ganancia neta"
                    value={fmtMXN(summary?.net || 0)}
                    icon={<Wallet className="text-slate-700" size={20} />}
                    accent={(summary?.net || 0) >= 0 ? "emerald" : "rose"}
                    testid="kpi-net"
                />
                <KpiCard
                    label="Transacciones"
                    value={String(summary?.transaction_count || 0)}
                    icon={<CarIcon className="text-slate-700" size={20} />}
                    accent="slate"
                    testid="kpi-count"
                />
            </div>

            {/* Timeline */}
            <Card className="border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-lg font-display font-medium">Línea de tiempo</CardTitle>
                        <p className="text-xs text-slate-500 mt-1">Ingresos vs Gastos por día</p>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="h-72 grid place-items-center text-slate-400 text-sm">Cargando…</div>
                    ) : (
                        <div className="h-72" data-testid="chart-timeline">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={summary?.timeline || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                    <XAxis dataKey="date" tick={CHART_TICK_STYLE} />
                                    <YAxis tick={CHART_TICK_STYLE} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                                    <Tooltip
                                        formatter={(v) => fmtMXN(v)}
                                        contentStyle={CHART_TOOLTIP_STYLE}
                                    />
                                    <Legend wrapperStyle={CHART_LEGEND_STYLE_12} />
                                    <Line type="monotone" dataKey="income" name="Ingresos" stroke="#10B981" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="expense" name="Gastos" stroke="#E11D48" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Pie Expenses */}
                <Card className="border-slate-200 shadow-sm lg:col-span-1">
                    <CardHeader>
                        <CardTitle className="text-lg font-display font-medium">Gastos por categoría</CardTitle>
                        <p className="text-xs text-slate-500 mt-1">Distribución del periodo</p>
                    </CardHeader>
                    <CardContent>
                        {expenseByCategory.length === 0 ? (
                            <div className="h-64 grid place-items-center text-sm text-slate-400">Sin datos</div>
                        ) : (
                            <div className="h-64" data-testid="chart-categories">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={expenseByCategory}
                                            dataKey="total"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={80}
                                            innerRadius={45}
                                        >
                                            {expenseByCategory.map((entry, idx) => (
                                                <Cell key={`${entry.category_id}-${entry.type}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(v) => fmtMXN(v)}
                                            contentStyle={CHART_TOOLTIP_STYLE}
                                        />
                                        <Legend wrapperStyle={CHART_LEGEND_STYLE_11} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Bar by car */}
                <Card className="border-slate-200 shadow-sm lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="text-lg font-display font-medium">Desempeño por auto</CardTitle>
                        <p className="text-xs text-slate-500 mt-1">
                            % de Ingresos que se fue en Gastos vs. quedó de Neto (columnas 100% apiladas)
                            {fleetAvgMarginPct !== null && (
                                <> · Margen promedio de la flotilla: <span className="font-medium text-indigo-600">{fleetAvgMarginPct.toFixed(0)}%</span></>
                            )}
                        </p>
                    </CardHeader>
                    <CardContent>
                        {performanceByCar.length === 0 ? (
                            <div className="h-64 grid place-items-center text-sm text-slate-400">Sin datos</div>
                        ) : (
                            <div className="h-64" data-testid="chart-by-car">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={performanceByCar}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                        <XAxis dataKey="label" tick={CHART_TICK_STYLE} />
                                        <YAxis
                                            tick={CHART_TICK_STYLE}
                                            tickFormatter={(v) => `${v}%`}
                                            domain={[0, (dataMax) => Math.max(dataMax, 100) + 10]}
                                        />
                                        <Tooltip content={<PerformanceStackedTooltip />} />
                                        <Legend wrapperStyle={CHART_LEGEND_STYLE_12} />
                                        <ReferenceLine y={100} stroke="#94A3B8" strokeDasharray="4 4" label={{ value: "100% Ingresos", position: "insideTopRight", fill: "#64748B", fontSize: 10 }} />
                                        {fleetAvgMarginPct !== null && (
                                            <ReferenceLine
                                                y={100 - fleetAvgMarginPct}
                                                stroke="#6366F1"
                                                strokeWidth={1.5}
                                                strokeDasharray="2 3"
                                                data-testid="fleet-avg-margin-line"
                                                label={{ value: `Margen promedio: ${fleetAvgMarginPct.toFixed(0)}%`, position: "insideBottomRight", fill: "#4F46E5", fontSize: 10 }}
                                            />
                                        )}
                                        <Bar dataKey="expense_pct" name="Gastos % de Ingresos" stackId="pct" fill="#E11D48" radius={[0, 0, 0, 0]} />
                                        <Bar dataKey="net_pct" name="Neto % de Ingresos" stackId="pct" fill="#10B981" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Per-car cards */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xl font-display font-medium text-slate-900">Detalle por auto</h2>
                    <Link to="/autos">
                        <Button variant="ghost" className="text-slate-700">
                            Gestionar flotilla <ArrowRight className="ml-1" size={16} />
                        </Button>
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(summary?.by_car || []).map((b) => {
                        const car = cars.find((c) => c.id === b.car_id);
                        if (!car) return null;
                        const expAlert = expirationAlert(expirationsByCar[b.car_id]);
                        return (
                            <Link key={b.car_id} to={`/autos/${b.car_id}`} data-testid={`car-card-${b.car_id}`}>
                                <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                                    <CardContent className="p-5">
                                        <div className="flex items-center gap-3">
                                            <div className="relative">
                                                <div
                                                    className={`w-12 h-12 rounded-md grid place-items-center font-semibold font-display ${netMarginBadge(ytdByCar[b.car_id]).cls}`}
                                                    title={netMarginBadge(ytdByCar[b.car_id]).title}
                                                    data-testid={`car-card-margin-badge-${b.car_id}`}
                                                >
                                                    {car.plate.slice(0, 3)}
                                                </div>
                                                {expAlert && (
                                                    <div
                                                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-600 text-white grid place-items-center ring-2 ring-white"
                                                        title={expAlert}
                                                        data-testid={`car-card-expiry-alert-${b.car_id}`}
                                                    >
                                                        <AlertTriangle size={11} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-medium text-slate-900">{car.brand} {car.model}</div>
                                                <div className="text-xs text-slate-500">{car.plate} · {car.driver_name || "Sin conductor"}</div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-3 mt-4">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-slate-500">Ingresos</div>
                                                <div className="text-sm font-semibold text-emerald-600 mt-0.5">{fmtMXN(b.income)}</div>
                                            </div>
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-slate-500">Gastos</div>
                                                <div className="text-sm font-semibold text-rose-600 mt-0.5">{fmtMXN(b.expense)}</div>
                                            </div>
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-slate-500">Neto</div>
                                                <div className={`text-sm font-semibold mt-0.5 ${b.net >= 0 ? "text-slate-900" : "text-rose-600"}`}>{fmtMXN(b.net)}</div>
                                            </div>
                                        </div>
                                        {ytdByCar[b.car_id] && (
                                            <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-slate-100" data-testid={`car-card-ytd-${b.car_id}`}>
                                                <div>
                                                    <div className="text-[9px] uppercase tracking-wider text-slate-400">Ingresos YTD</div>
                                                    <div className="text-xs font-medium text-emerald-600/80 mt-0.5">{fmtMXN(ytdByCar[b.car_id].income)}</div>
                                                    {ytdPrevByCar[b.car_id] !== undefined && (
                                                        <div className={`text-[10px] mt-0.5 ${ytdDelta(ytdByCar[b.car_id].income, ytdPrevByCar[b.car_id]?.income, false, prevYearLabel).cls}`} data-testid={`car-card-ytd-income-delta-${b.car_id}`}>
                                                            {ytdDelta(ytdByCar[b.car_id].income, ytdPrevByCar[b.car_id]?.income, false, prevYearLabel).text}
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="text-[9px] uppercase tracking-wider text-slate-400">Gastos YTD</div>
                                                    <div className="text-xs font-medium text-rose-600/80 mt-0.5">{fmtMXN(ytdByCar[b.car_id].expense)}</div>
                                                    {ytdPrevByCar[b.car_id] !== undefined && (
                                                        <div className={`text-[10px] mt-0.5 ${ytdDelta(ytdByCar[b.car_id].expense, ytdPrevByCar[b.car_id]?.expense, true, prevYearLabel).cls}`} data-testid={`car-card-ytd-expense-delta-${b.car_id}`}>
                                                            {ytdDelta(ytdByCar[b.car_id].expense, ytdPrevByCar[b.car_id]?.expense, true, prevYearLabel).text}
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="text-[9px] uppercase tracking-wider text-slate-400">Neto YTD</div>
                                                    <div className={`text-xs font-medium mt-0.5 ${ytdByCar[b.car_id].net >= 0 ? "text-slate-700" : "text-rose-600/80"}`}>{fmtMXN(ytdByCar[b.car_id].net)}</div>
                                                    {ytdPrevByCar[b.car_id] !== undefined && (
                                                        <div className={`text-[10px] mt-0.5 ${ytdDelta(ytdByCar[b.car_id].net, ytdPrevByCar[b.car_id]?.net, false, prevYearLabel).cls}`} data-testid={`car-card-ytd-net-delta-${b.car_id}`}>
                                                            {ytdDelta(ytdByCar[b.car_id].net, ytdPrevByCar[b.car_id]?.net, false, prevYearLabel).text}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </Link>
                        );
                    })}
                </div>
            </div>

            {/* Bitácora de servicios mecánicos — consolidado */}
            <Card className="border-slate-200 shadow-sm" data-testid="fleet-service-logs-section">
                <CardHeader className="flex flex-row items-start justify-between gap-3">
                    <div>
                        <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                            <Wrench size={18} className="text-slate-500" />
                            Bitácora de servicios mecánicos — Consolidado
                        </CardTitle>
                        <p className="text-xs text-slate-500 mt-1">Servicios y reparaciones de toda la flotilla en el periodo seleccionado.</p>
                    </div>
                    {serviceLogs.length > 0 && (
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Total en servicios</div>
                            <div className="text-sm font-medium text-slate-900">{fmtMXN(totalServiceCost)}</div>
                        </div>
                    )}
                </CardHeader>
                <CardContent className="p-0">
                    {serviceLogs.length === 0 ? (
                        <div className="px-6 py-10 text-center text-slate-400 text-sm">No hay servicios registrados en este periodo.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                        <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                                        <th className="text-left px-4 py-3 font-semibold">Servicio</th>
                                        <th className="text-left px-4 py-3 font-semibold">Tipo</th>
                                        <th className="text-left px-4 py-3 font-semibold">Taller</th>
                                        <th className="text-left px-4 py-3 font-semibold">Kilometraje</th>
                                        <th className="text-left px-4 py-3 font-semibold">Próximo servicio</th>
                                        <th className="text-right px-4 py-3 font-semibold">Costo</th>
                                        <th className="px-4 py-3" />
                                    </tr>
                                </thead>
                                {groupedServiceLogs.map(({ car, logs, subtotal }, groupIdx) => (
                                    <tbody key={car.id} className={groupIdx > 0 ? "border-t-8 border-slate-100" : undefined} data-testid={`fleet-service-log-group-${car.id}`}>
                                        <tr className="bg-slate-100/70">
                                            <td colSpan={6} className="px-4 py-2">
                                                <Link to={`/autos/${car.id}`} className="font-semibold text-slate-800 hover:underline">
                                                    {car.plate} · {car.brand} {car.model}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-2 text-right font-semibold text-slate-800">{fmtMXN(subtotal)}</td>
                                            <td className="px-4 py-2" />
                                        </tr>
                                        {logs.map((s) => (
                                            <tr key={s.id} className="border-t border-slate-100" data-testid={`fleet-service-log-${s.id}`}>
                                                <td className="px-4 py-3 text-slate-900 font-medium">{fmtDate(s.date)}</td>
                                                <td className="px-4 py-3 text-slate-700">
                                                    {s.service_type}
                                                    {s.notes && <div className="text-xs text-slate-400 mt-0.5">{s.notes}</div>}
                                                </td>
                                                <td className="px-4 py-3" data-testid={`fleet-service-log-kind-${s.id}`}>
                                                    {s.service_kind ? (
                                                        <Badge className={s.service_kind === "mano_de_obra" ? "bg-sky-100 text-sky-700 hover:bg-sky-100" : "bg-violet-100 text-violet-700 hover:bg-violet-100"}>
                                                            {s.service_kind === "mano_de_obra" ? "Mano de obra" : "Refacción"}
                                                        </Badge>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-4 py-3 text-slate-700">{s.supplier_id ? supplierName(s.supplier_id) : "—"}</td>
                                                <td className="px-4 py-3 text-slate-700">{s.mileage != null ? `${Number(s.mileage).toLocaleString("es-MX")} km` : "—"}</td>
                                                <td className="px-4 py-3 text-slate-700">
                                                    {s.next_service_date ? fmtDate(s.next_service_date) : (s.next_service_mileage != null ? `${Number(s.next_service_mileage).toLocaleString("es-MX")} km` : "—")}
                                                </td>
                                                <td className="px-4 py-3 text-right text-slate-700">{fmtMXN(s.cost)}</td>
                                                <td className="px-4 py-3 text-right">
                                                    {s.receipt_path && (
                                                        <a
                                                            href={withToken(`${API}/files/${s.receipt_path}`)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="p-1.5 rounded hover:bg-slate-100 inline-flex"
                                                            data-testid={`fleet-service-log-receipt-${s.id}`}
                                                        >
                                                            <FileImage size={14} className="text-slate-500" />
                                                        </a>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                ))}
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {canWrite && (
                <div className="flex justify-end">
                    <Link to="/transacciones">
                        <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="cta-add-transaction">
                            <Plus size={16} className="mr-1" /> Registrar movimiento
                        </Button>
                    </Link>
                </div>
            )}
        </div>
    );
}

function KpiCard({ label, value, icon, accent, testid }) {
    const accentBar = {
        emerald: "bg-emerald-500",
        rose: "bg-rose-500",
        slate: "bg-slate-700",
    }[accent] || "bg-slate-700";
    return (
        <Card className="kpi-card border-slate-200 shadow-sm overflow-hidden" data-testid={testid}>
            <div className={`h-1 ${accentBar}`} />
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">{label}</div>
                    {icon}
                </div>
                <div className="text-3xl md:text-4xl font-semibold tracking-tighter text-slate-900 mt-3 font-display">
                    {value}
                </div>
            </CardContent>
        </Card>
    );
}
